import { supabase } from "@/integrations/supabase/client";
import { closeOrderLocal, sendItemsLocal, getCurrentOrder, isLocalServerAvailable, type SendItemsData } from "@/lib/dataGateway";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import type { CartItem, OrderInfo, ComboCartItem } from "./useCart";
import type { PaymentEntry } from "@/components/pdv/CheckoutDialog";
import { printOrderByLocations, printCustomerReceipt } from "@/lib/order-printing";
import { getPrintMode } from "@/lib/print-dispatcher";
import { printExitRelease } from "@/lib/exit-release-printing";
import { getTerminalName } from "@/lib/qz-tray";
import { createKdsItemsForOrder } from "@/lib/kds-routing";
import { deductStockFromOrder } from "@/lib/stock-deduction";
import { savePendingOrder, deleteActiveConsumption as deleteActiveConsumptionIDB, getActiveConsumption as getActiveConsumptionIDB } from "@/lib/offline-storage";
import { useWhatsAppNotification } from "./useWhatsAppNotification";
import { useOperationLogs } from "./useOperationLogs";
import { withRetry, isRetryableError } from "@/lib/supabase-retry";
import { useSQLiteLocal } from "./useSQLiteLocal";
import { useSQLiteSync } from "./useSQLiteSync";
import { getIsOnline } from "@/lib/connectivity";
import { printProductionTicketOffline } from "@/lib/offline-production-print";

// Helper to convert combo items to RPC format
function comboItemsToRpcFormat(comboItems: ComboCartItem[]) {
  return comboItems.map((item) => ({
    combo_id: item.combo.id,
    product_id: null,
    product_name: item.combo.name,
    quantity: item.quantity,
    unit_price: item.calculatedPrice,
    total_price: item.calculatedPrice * item.quantity,
    notes: item.notes || null,
    combo_selections: item.selection,
    selected_addons: [],
  }));
}

export function useOrders() {
  const { activeRestaurant } = useRestaurantContext();
  const { notifyDiscount } = useWhatsAppNotification();
  const { logOperation } = useOperationLogs();
  
  // SQLite for offline support
  const { 
    isReady: sqliteReady,
    closeOrderOffline: localCloseOrder,
    getOrderItems: getLocalOrderItems,
    deleteActiveConsumption,
    upsertActiveConsumption,
    getActiveConsumption,
    updateTableStatus,
    updateCommandStatus,
    addOrderPayment: localAddOrderPayment,
    getProductById: getLocalProductById,
    getProductionLocationById: getLocalProductionLocationById,
    markLocalOrderItemsPrinted,
  } = useSQLiteLocal();
  const { isOnline } = useSQLiteSync();

  const createOrderOnline = async (
    items: CartItem[],
    orderInfo: OrderInfo,
    discount: number = 0,
    payments: PaymentEntry[] = [],
    serviceTax: number = 0,
    couvertQuantity: number = 0,
    couvertAmount: number = 0,
    tableId?: string,
    commandId?: string,
    entryDifference?: number,
    entryId?: string,
    peopleCount?: number
  ) => {
    if (!activeRestaurant || items.length === 0) return null;

    const subtotal = items.reduce(
      (sum, item) => sum + Number(item.product.price) * item.quantity,
      0
    );
    // Include entry difference in total
    const entryAmount = entryDifference || 0;
    const total = subtotal - discount + serviceTax + couvertAmount + entryAmount;

    // Determine if this is a closed order (paid immediately)
    const isClosedOrder = payments.length > 0;

    // For backward compatibility, use first payment method if only one
    const primaryPayment = isClosedOrder ? payments[0] : null;
    const totalFees = payments.reduce((sum, p) => sum + p.feeAmount, 0);

    // Create order - status depends on whether payments were provided
    const { data: order, error: orderError } = await supabase
      .from("orders")
      .insert({
        restaurant_id: activeRestaurant.id,
        order_type: orderInfo.type,
        table_number: orderInfo.type === "counter" ? null : (orderInfo.tableNumber || null),
        tab_number: orderInfo.type === "counter" ? null : (orderInfo.tabNumber || null),
        customer_name: orderInfo.customerName || null,
        waiter_name: orderInfo.waiterName || null,
        notes: orderInfo.notes || null,
        subtotal,
        discount,
        additional_fees: serviceTax,
        couvert_quantity: couvertQuantity,
        couvert_amount: couvertAmount,
        total,
        status: orderInfo.type === "counter"
          ? "paid"
          : (isClosedOrder ? "delivered" : "preparing"),
        payment_method_id: primaryPayment?.paymentMethod.id || null,
        payment_label: primaryPayment?.paymentMethod.name || null,
        payment_fee: totalFees,
        people_count: peopleCount || orderInfo.peopleCount || null,
      })
      .select()
      .single();

    if (orderError) {
      console.error("[createOrderOnline] insert orders failed:", {
        code: (orderError as any).code,
        message: orderError.message,
        details: (orderError as any).details,
        hint: (orderError as any).hint,
      });
      throw orderError;
    }

    // Update table status based on whether order is paid
    if (tableId && orderInfo.type === "table") {
      if (isClosedOrder) {
        // Order paid immediately - release table
        const { error: tableError } = await supabase
          .from("tables")
          .update({ 
            status: "available", 
            current_order_id: null,
            name: null 
          })
          .eq("id", tableId);
        
        if (tableError) {
          console.error("[createOrderOnline] Erro ao liberar mesa:", tableError);
        } else {
          console.log("[createOrderOnline] Mesa liberada (checkout direto):", tableId);
        }
      } else {
        // Order in progress - occupy table
        const { error: tableError } = await supabase
          .from("tables")
          .update({ status: "occupied", current_order_id: order.id })
          .eq("id", tableId);
        
        if (tableError) {
          console.error("Error updating table status:", tableError);
        }
      }
    }

    // Update command status based on whether order is paid
    if (commandId && orderInfo.type === "tab") {
      if (isClosedOrder) {
        // Order paid immediately - release command
        const { error: commandError } = await supabase
          .from("commands")
          .update({ 
            status: "available", 
            current_order_id: null,
            customer_name: null
          })
          .eq("id", commandId);
        
        if (commandError) {
          console.error("[createOrderOnline] Erro ao liberar comanda:", commandError);
        } else {
          console.log("[createOrderOnline] Comanda liberada (checkout direto):", commandId);
        }
      } else {
        // Order in progress - use command
        const { error: commandError } = await supabase
          .from("commands")
          .update({ 
            status: "in_use", 
            current_order_id: order.id,
            customer_name: orderInfo.customerName || null
          })
          .eq("id", commandId);
        
        if (commandError) {
          console.error("Error updating command status:", commandError);
        }
      }
    }

    // Close the command entry if exists
    if (entryId) {
      await supabase
        .from("command_entries")
        .update({
          status: "closed",
          consumed_amount: subtotal,
          closed_at: new Date().toISOString(),
          order_id: order.id,
        })
        .eq("id", entryId);
    }

    // Create order items
    const orderItems = items.map((item) => ({
      order_id: order.id,
      product_id: item.product.id,
      product_name: item.product.name,
      quantity: item.quantity,
      unit_price: Number(item.product.price),
      total_price: Number(item.product.price) * item.quantity,
      notes: item.notes || null,
    }));

    const { error: itemsError } = await supabase
      .from("order_items")
      .insert(orderItems);

    if (itemsError) {
      console.error("[createOrderOnline] insert order_items failed:", {
        code: (itemsError as any).code,
        message: itemsError.message,
        details: (itemsError as any).details,
        hint: (itemsError as any).hint,
      });
      // Rollback order
      await supabase.from("orders").delete().eq("id", order.id);
      throw itemsError;
    }

    // Create order payments for split payments
    if (payments.length > 0) {
      const orderPayments = payments.map((payment) => ({
        order_id: order.id,
        payment_method_id: payment.paymentMethod.id,
        amount: payment.amount,
        fee_amount: payment.feeAmount,
      }));

      const { error: paymentsError } = await supabase
        .from("order_payments")
        .insert(orderPayments);

      if (paymentsError) {
        console.error("Error creating order payments:", paymentsError);
        // Don't fail the order, just log the error
      }
    }

    toast.success(`Pedido #${order.order_number} criado com sucesso!`);

    // Log the order creation
    await logOperation("create", "order", order.id, {
      order_number: order.order_number,
      total,
      items_count: items.length,
      discount,
    });

    // Send WhatsApp notification if discount was applied
    if (discount > 0) {
      notifyDiscount(order.order_number, discount, total);
    }

    // Deduct stock based on product recipes
    try {
      const stockResult = await deductStockFromOrder(activeRestaurant.id, order.id, items);
      
      if (stockResult.deductions.length > 0) {
        console.log(`Stock deducted for ${stockResult.deductions.length} ingredients`);
        
        // Check for depleted stock and warn
        const depletedIngredients = stockResult.deductions.filter(d => d.newStock <= 0);
        if (depletedIngredients.length > 0) {
          toast.warning(`Estoque zerado: ${depletedIngredients.map(d => d.ingredientName).join(", ")}`);
        }
      }
      
      if (stockResult.errors.length > 0) {
        console.error("Stock deduction errors:", stockResult.errors);
      }
    } catch (stockError) {
      console.error("Error deducting stock:", stockError);
      // Don't fail the order if stock deduction fails
    }

    // Auto-print to production locations
    try {
      // For counter (balcão): use generate-print-jobs edge function for kitchen
      // (works even if QZ Tray is offline on the PC). Skip printOrderByLocations
      // to avoid duplicate prints. Customer receipt is printed below.
      if (orderInfo.type === "counter") {
        // Fase 4: em local_service, o servidor local imprime — NÃO chamar a edge (evita duplicar).
        const printMode = await getPrintMode(activeRestaurant.id);
        if (printMode === "local_service") {
          console.log("[createOrderOnline] print_mode=local_service — pulando generate-print-jobs (servidor local imprime)");
        } else try {
          const { data: printData, error: printError } = await supabase.functions.invoke(
            "generate-print-jobs",
            {
              body: {
                order_id: order.id,
                restaurant_id: activeRestaurant.id,
              },
            }
          );
          if (printError) {
            console.error("[Balcão] generate-print-jobs falhou:", printError);
          } else {
            console.log(
              "[createOrderOnline] generate-print-jobs OK para pedido counter",
              order.order_number,
              printData
            );
          }
        } catch (edgeErr) {
          console.error("[createOrderOnline] generate-print-jobs failed (non-blocking):", edgeErr);
        }
      } else {
        await printOrderByLocations(
          order.order_number,
          orderItems.map(item => ({
            product_id: item.product_id || "",
            product_name: item.product_name,
            quantity: item.quantity,
            notes: item.notes,
          })),
          { ...orderInfo, sourceName: getTerminalName() || undefined },
          activeRestaurant.id
        );
      }

      // Mark items as printed after successful print
      await supabase
        .from("order_items")
        .update({ printed_at: new Date().toISOString() })
        .eq("order_id", order.id)
        .is("printed_at", null);

      // Print customer receipt
      let entryReceiptData: {
        entryTypeName?: string;
        entryAmount?: number;
        entryMode?: "fixed" | "minimum_consumption";
        entryDifference?: number;
      } = {};
      if (entryId) {
        try {
          const { data: entryRow } = await supabase
            .from("command_entries")
            .select("entry_type_name, amount, entry_mode")
            .eq("id", entryId)
            .maybeSingle();
          if (entryRow) {
            entryReceiptData.entryTypeName = entryRow.entry_type_name;
            entryReceiptData.entryAmount = Number(entryRow.amount);
            entryReceiptData.entryMode = entryRow.entry_mode as "fixed" | "minimum_consumption";
            if (entryRow.entry_mode === "minimum_consumption") {
              const diff = Number(entryRow.amount) - subtotal;
              if (diff > 0) entryReceiptData.entryDifference = diff;
            }
          }
        } catch (e) {
          console.warn("[createOrderOnline] Erro ao buscar entrada para recibo:", e);
        }
      }

      await printCustomerReceipt({
        orderNumber: order.order_number,
        items: orderItems.map(item => ({
          name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
        })),
        subtotal,
        serviceTax: serviceTax,
        discount,
        total,
        paymentMethod: payments[0]?.paymentMethod.name || "Não informado",
        customerName: orderInfo.customerName,
        restaurantName: activeRestaurant.name,
        restaurantId: activeRestaurant.id,
        couvertAmount: couvertAmount > 0 ? couvertAmount * (couvertQuantity || 1) : undefined,
        couvertQuantity: couvertQuantity || undefined,
        ...entryReceiptData,
      });
    } catch (printError) {
      console.error("Error printing order:", printError);
      // Don't fail the order if printing fails
    }

    // Create KDS ticket items in background for non-delivered orders
    if (order.status !== "delivered") {
      const { data: createdItems } = await supabase
        .from("order_items")
        .select("id, product_id, product_name, quantity, notes, combo_id, combo_selections")
        .eq("order_id", order.id);

      if (createdItems && createdItems.length > 0) {
        createKdsItemsForOrder(activeRestaurant.id, order.id, createdItems)
          .catch(err => console.error("Background KDS routing error:", err));
      }
    }

    // Immediately cache to SQLite for offline resilience (non-closed orders with table/command)
    if (!isClosedOrder && sqliteReady) {
      try {
        const resourceId = tableId || commandId || null;
        const resourceType = tableId ? "table" : commandId ? "command" : null;
        const resourceNumber = orderInfo.tableNumber || orderInfo.tabNumber || null;
        if (resourceId && resourceType && resourceNumber) {
          // MERGE: preserve existing cached items from previous orders on the same table
          const existingConsumption = getActiveConsumption(resourceId);
          const existingItems = existingConsumption?.items || [];
          
          const newItems = orderItems.map(item => ({
            id: crypto.randomUUID(),
            order_id: order.id,
            product_id: item.product_id,
            combo_id: null,
            product_name: item.product_name,
            quantity: item.quantity,
            unit_price: item.unit_price,
            total_price: item.total_price,
            notes: item.notes,
            status: "preparing",
            sent_to_kitchen: 1,
            created_at: new Date().toISOString(),
            synced: 1,
            server_id: null,
          }));
          
          // Combine existing items (from prior orders) with new items
          const mergedItems = [...existingItems, ...newItems];
          
          upsertActiveConsumption(
            resourceId,
            resourceType as "table" | "command",
            order.id,
            order.order_number ?? null,
            resourceNumber,
            mergedItems
          );
          console.log(`[createOrderOnline] Cached to SQLite: ${existingItems.length} existing + ${newItems.length} new items`);

          // Also update table/command status in SQLite so the map reflects occupied state offline
          if (tableId) {
            updateTableStatus(tableId, "occupied", order.id);
            console.log("[createOrderOnline] SQLite table status set to occupied:", tableId);
          } else if (commandId) {
            updateCommandStatus(commandId, "in_use", order.id, orderInfo.customerName || null);
            console.log("[createOrderOnline] SQLite command status set to in_use:", commandId);
          }
        }
      } catch (cacheErr) {
        console.error("[createOrderOnline] SQLite cache error (non-blocking):", cacheErr);
      }
    }

    return order;
  };

  const createOrderOffline = async (
    items: CartItem[],
    orderInfo: OrderInfo,
    discount: number = 0,
    payments: PaymentEntry[] = [],
    serviceTax: number = 0,
    couvertQuantity: number = 0,
    couvertAmount: number = 0,
    tableId?: string,
    commandId?: string,
    entryDifference?: number,
    entryId?: string,
    peopleCount?: number
  ) => {
    if (!activeRestaurant || items.length === 0) return null;

    const subtotal = items.reduce(
      (sum, item) => sum + Number(item.product.price) * item.quantity,
      0
    );
    const entryAmount = entryDifference || 0;
    const total = subtotal - discount + serviceTax + couvertAmount + entryAmount;

    try {
      const offlineId = await savePendingOrder({
        restaurantId: activeRestaurant.id,
        items: items.map((item) => ({
          productId: item.product.id,
          productName: item.product.name,
          quantity: item.quantity,
          unitPrice: Number(item.product.price),
          notes: item.notes,
        })),
        orderInfo: {
          type: orderInfo.type,
          tableNumber: orderInfo.tableNumber,
          tabNumber: orderInfo.tabNumber,
          customerName: orderInfo.customerName,
          notes: orderInfo.notes,
        },
        discount,
        payments: payments.map((p) => ({
          paymentMethodId: p.paymentMethod.id,
          paymentMethodName: p.paymentMethod.name,
          amount: p.amount,
          feeAmount: p.feeAmount,
        })),
        serviceTax,
        couvertQuantity,
        couvertAmount,
        tableId,
        commandId,
        subtotal,
        total,
      });

      toast.success('Pedido salvo offline!', {
        description: 'Será sincronizado quando a conexão for restaurada.',
      });

      // Return a mock order object for UI consistency
      return {
        id: offlineId,
        order_number: `OFF-${Date.now()}`,
        isOffline: true,
      };
    } catch (error) {
      console.error('Error saving offline order:', error);
      toast.error('Erro ao salvar pedido offline');
      return null;
    }
  };

  const createOrder = async (
    items: CartItem[],
    orderInfo: OrderInfo,
    discount: number = 0,
    payments: PaymentEntry[] = [],
    serviceTax: number = 0,
    couvertQuantity: number = 0,
    couvertAmount: number = 0,
    tableId?: string,
    commandId?: string,
    entryDifference?: number,
    entryId?: string,
    peopleCount?: number
  ) => {
    if (!activeRestaurant || items.length === 0) return null;

    // Check if online
    if (getIsOnline()) {
      try {
        return await createOrderOnline(
          items,
          orderInfo,
          discount,
          payments,
          serviceTax,
          couvertQuantity,
          couvertAmount,
          tableId,
          commandId,
          entryDifference,
          entryId,
          peopleCount
        );
      } catch (error) {
        console.error('Error creating order online:', error);
        // Counter (balcão) is a synchronous cash sale — DO NOT silently fall back to
        // offline. Surface the real error so the user can retry and we can diagnose.
        if (orderInfo.type === "counter") {
          const msg = (error as any)?.message || 'Erro desconhecido';
          toast.error(`Falha ao registrar venda do Balcão: ${msg}`);
          return null;
        }
        toast.warning('Falha na conexão, salvando offline...');
        return await createOrderOffline(
          items,
          orderInfo,
          discount,
          payments,
          serviceTax,
          couvertQuantity,
          couvertAmount,
          tableId,
          commandId,
          entryDifference,
          entryId,
          peopleCount
        );
      }
    } else {
      return await createOrderOffline(
        items,
        orderInfo,
        discount,
        payments,
        serviceTax,
        couvertQuantity,
        couvertAmount,
        tableId,
        commandId,
        entryDifference,
        entryId,
        peopleCount
      );
    }
  };

  // Helper function for retry with exponential backoff
  const sendWithRetry = async <T>(
    fn: () => Promise<T>,
    maxRetries: number = 3
  ): Promise<T> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error: any) {
        const isNetworkError = 
          !getIsOnline() ||
          error.message?.includes("Failed to fetch") ||
          error.message?.includes("NetworkError") ||
          error.message?.includes("network") ||
          error.code === "PGRST301";
        
        if (attempt === maxRetries || !isNetworkError) {
          throw error;
        }
        
        // Exponential backoff: 500ms, 1s, 2s
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 250));
      }
    }
    throw new Error("Max retries exceeded");
  };

  const sendItemsToOrder = async (
    items: CartItem[],
    orderInfo: OrderInfo,
    tableId?: string,
    commandId?: string,
    peopleCount?: number,
    comboItems: ComboCartItem[] = [],
    activeRodizioComboId?: string
  ) => {
    if (!activeRestaurant || (items.length === 0 && comboItems.length === 0)) return null;

    // Prepare product items for RPC call
    const productRpcItems = items.map((item) => ({
      product_id: item.product.id,
      product_name: item.product.name,
      quantity: item.quantity,
      unit_price: Number(item.product.price),
      total_price: Number(item.product.price) * item.quantity,
      notes: item.notes || null,
      combo_id: null,
      combo_selections: [],
      selected_addons: [],
    }));

    // Prepare combo items for RPC call
    const comboRpcItems = comboItemsToRpcFormat(comboItems);

    // Combine all items
    const rpcItems = [...productRpcItems, ...comboRpcItems];

    try {
      // EARLY OFFLINE CHECK: só cai para o SQLite do browser se NÃO há internet NEM servidor local
      // alcançável. Internet caída mas LAN ok (servidor local de pé) → segue para o gateway
      // (sendItemsLocal), que grava no servidor local — o cenário mais comum no restaurante.
      if (!getIsOnline() && !(await isLocalServerAvailable())) {
        throw Object.assign(new Error("Device is offline"), { __forceOfflineFallback: true });
      }

      // === FAST-PATH: núcleo (resolve-ou-cria pedido + itens + ocupa mesa/comanda) no servidor
      //     local (gateway) → a nuvem recebe via sync. Substitui o RPC no ramo online. O pós-
      //     processamento abaixo (impressão via rpcItems, KDS, printed_at, estoque) segue igual.
      //     Rodízio (activeRodizioComboId) NÃO vai pro local (endpoint não trata) → usa a nuvem.
      //     Fallback: local indisponível OU 4xx (rolled back, sem duplo-write) → RPC da nuvem.
      let result: SendItemsData | null = null;
      if (!activeRodizioComboId) {
        const local = await sendItemsLocal({
          restaurant_id: activeRestaurant.id,
          table_id: tableId || null,
          command_id: commandId || null,
          order_type: orderInfo.type,
          table_number: orderInfo.tableNumber || null,
          tab_number: orderInfo.tabNumber || null,
          customer_name: orderInfo.customerName || null,
          waiter_name: orderInfo.waiterName || null,
          notes: orderInfo.notes || null,
          items: rpcItems,
          people_count: peopleCount || orderInfo.peopleCount || null,
          is_internal: true,
        });
        if (local.kind === "ok") {
          result = local.data as SendItemsData;
          if (import.meta.env.DEV) console.debug("[sendItemsToOrder] itens lançados no servidor local (nuvem via sync)");
        }
        // 'unavailable' | 'businessError' → cai para o RPC da nuvem (local fez ROLLBACK; sem duplo-write)
      }

      // RPC da nuvem (atômico, com retry) — fallback do local OU caminho do rodízio.
      if (!result) {
        result = await sendWithRetry(async () => {
          const { data, error } = await supabase.rpc("send_order_items_fast", {
            p_restaurant_id: activeRestaurant.id,
            p_table_id: tableId || null,
            p_command_id: commandId || null,
            p_order_type: orderInfo.type,
            p_table_number: orderInfo.tableNumber || null,
            p_tab_number: orderInfo.tabNumber || null,
            p_customer_name: orderInfo.customerName || null,
            p_waiter_name: orderInfo.waiterName || null,
            p_notes: orderInfo.notes || null,
            p_items: rpcItems,
            p_people_count: peopleCount || orderInfo.peopleCount || null,
            p_active_rodizio_combo_id: activeRodizioComboId || null,
            p_is_internal: true,
          } as any);

          if (error) throw error;
          return data as unknown as SendItemsData;
        });
      }

      if (!result) {
        throw new Error("Falha ao criar pedido");
      }

      const orderId = result.order_id;
      const orderNumber = result.order_number;

      // Deduce stock in background (non-blocking)
      deductStockFromOrder(activeRestaurant.id, orderId, items, comboItems)
        .then(stockResult => {
          if (stockResult.deductions.length > 0) {
            console.log(`Stock deducted for ${stockResult.deductions.length} ingredients`);
            const depletedIngredients = stockResult.deductions.filter((d) => d.newStock <= 0);
            if (depletedIngredients.length > 0) {
              toast.warning(`Estoque zerado: ${depletedIngredients.map((d) => d.ingredientName).join(", ")}`);
            }
          }
        })
        .catch(err => console.error("Background stock deduction error:", err));

      // Print in background (non-blocking)
      printOrderByLocations(
        orderNumber,
        rpcItems.map((item) => ({
          product_id: item.product_id || "",
          product_name: item.product_name,
          quantity: item.quantity,
          notes: item.notes,
          combo_id: item.combo_id,
          combo_selections: item.combo_selections,
        })),
        { ...orderInfo, sourceName: getTerminalName() || undefined },
        activeRestaurant.id
      ).then(() => {
        // Mark items as printed after successful print
        supabase
          .from("order_items")
          .update({ printed_at: new Date().toISOString() })
          .eq("order_id", orderId)
          .is("printed_at", null)
          .then(({ error }) => {
            if (error) console.error("Error marking items as printed:", error);
          });
      }).catch(err => console.error("Background print error:", err));

      // Create KDS ticket items in background (non-blocking)
      (async () => {
        try {
          const { data: newItems } = await supabase
            .from("order_items")
            .select("id, product_id, product_name, quantity, notes, combo_id, combo_selections")
            .eq("order_id", orderId)
            .is("printed_at", null);

          if (newItems && newItems.length > 0) {
            await createKdsItemsForOrder(activeRestaurant.id, orderId, newItems);
          }
        } catch (err) {
          console.error("Background KDS routing error:", err);
        }
      })();

      // ====== ONLINE SUCCESS CACHE: Update SQLite for offline resilience ======
      if (sqliteReady && (tableId || commandId)) {
        try {
          const entityId = tableId || commandId!;
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          if (uuidRegex.test(entityId)) {
            const entityType: 'table' | 'command' = tableId ? 'table' : 'command';
            const number = orderInfo.tableNumber || orderInfo.tabNumber || '';

            // Get existing cached items for this entity (merge strategy)
            const existingCache = getActiveConsumption(entityId);
            const existingItems = existingCache?.items || [];

            // Convert RPC items to local format
            const newItems = rpcItems.map(item => ({
              id: crypto.randomUUID(),
              order_id: orderId,
              product_id: item.product_id,
              combo_id: item.combo_id,
              product_name: item.product_name,
              quantity: item.quantity,
              unit_price: item.unit_price,
              total_price: item.total_price,
              notes: item.notes,
              status: 'preparing',
              sent_to_kitchen: 1,
              created_at: new Date().toISOString(),
              synced: 1,
              server_id: null,
            }));

            const mergedItems = [...existingItems, ...newItems];
            upsertActiveConsumption(entityId, entityType, orderId, orderNumber, number, mergedItems);

            if (tableId) {
              updateTableStatus(tableId, 'occupied', orderId);
            } else if (commandId) {
              updateCommandStatus(commandId, 'in_use', orderId, orderInfo.customerName);
            }
            console.log(`[sendItemsToOrder] Online success: Cached ${newItems.length} items for ${entityType} ${number}`);
          }
        } catch (cacheErr) {
          console.error("[sendItemsToOrder] SQLite cache error (non-blocking):", cacheErr);
        }
      }

      toast.success(`Itens enviados para o pedido #${orderNumber}!`);

      return { id: orderId, order_number: orderNumber };
    } catch (error: any) {
      console.error("Error sending items:", error);
      
      // Check if it's a network error for offline fallback
      const isNetworkError = 
        !getIsOnline() ||
        error.__forceOfflineFallback ||
        error.message?.includes("Failed to fetch") ||
        error.message?.includes("NetworkError") ||
        error.message?.includes("network");

      if (isNetworkError) {
        // Try to save offline
        try {
          const subtotal = items.reduce(
            (sum, item) => sum + Number(item.product.price) * item.quantity,
            0
          );
          
          const offlineId = await savePendingOrder({
            restaurantId: activeRestaurant.id,
            items: items.map((item) => ({
              productId: item.product.id,
              productName: item.product.name,
              quantity: item.quantity,
              unitPrice: Number(item.product.price),
              notes: item.notes,
            })),
            orderInfo: {
              type: orderInfo.type,
              tableNumber: orderInfo.tableNumber,
              tabNumber: orderInfo.tabNumber,
              customerName: orderInfo.customerName,
              notes: orderInfo.notes,
            },
            discount: 0,
            payments: [],
            serviceTax: 0,
            couvertQuantity: 0,
            couvertAmount: 0,
            tableId,
            commandId,
            subtotal,
            total: subtotal,
          });

          // ====== OFFLINE CACHE: Update SQLite for immediate visualization ======
          if (sqliteReady && (tableId || commandId)) {
            const entityId = tableId || commandId!;
            
            // VALIDATION: Ensure entityId is a valid UUID (not an offlineId)
            const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            if (!uuidRegex.test(entityId)) {
              console.error('[sendItemsToOrder] Invalid entityId (not a UUID):', entityId);
            } else {
              const entityType: 'table' | 'command' = tableId ? 'table' : 'command';
              const number = orderInfo.tableNumber || orderInfo.tabNumber || '';
              
              // Get existing cached items for this entity
              const existingCache = getActiveConsumption(entityId);
              const existingItems = existingCache?.items || [];
              
              // Convert cart items to local format
              const newItems = items.map(item => ({
                id: crypto.randomUUID(),
                order_id: offlineId,
                product_id: item.product.id,
                combo_id: null,
                product_name: item.product.name,
                quantity: item.quantity,
                unit_price: Number(item.product.price),
                total_price: Number(item.product.price) * item.quantity,
                notes: item.notes || null,
                status: 'pending',
                sent_to_kitchen: 0,
                created_at: new Date().toISOString(),
                synced: 0,
                server_id: null,
              }));
              
              // Merge with existing items
              const allItems = [...existingItems, ...newItems];
              
              // Cache for offline visualization
              upsertActiveConsumption(
                entityId,
                entityType,
                offlineId,
                null, // order_number - will be set after sync
                number,
                allItems
              );
              
              // Update table/command status in local SQLite
              if (tableId) {
                updateTableStatus(tableId, 'occupied', offlineId);
              } else if (commandId) {
                updateCommandStatus(commandId, 'in_use', offlineId, orderInfo.customerName);
              }
              
              console.log(`[sendItemsToOrder] Offline: Cached ${newItems.length} items for ${entityType} ${number}`);

              // Print production tickets locally via QZ Tray (non-blocking).
              // NOTE: items here are persisted only in IndexedDB pending_orders
              // (not SQLite order_items), so we cannot mark printed_at against
              // them. On reconnect, useOfflineSync re-issues the print via
              // printOrderByLocations — duplicate print risk is accepted as the
              // fallback path when QZ Tray local print succeeds offline.
              try {
                const printedIds = await printProductionTicketOffline({
                  items: newItems.map((ni) => ({
                    id: ni.id,
                    product_id: ni.product_id,
                    combo_id: ni.combo_id,
                    product_name: ni.product_name,
                    quantity: ni.quantity,
                    notes: ni.notes,
                  })),
                  orderInfo: {
                    type: orderInfo.type,
                    tableNumber: orderInfo.tableNumber,
                    tabNumber: orderInfo.tabNumber,
                    customerName: orderInfo.customerName,
                    notes: orderInfo.notes,
                  },
                  orderNumber: null,
                  restaurantId: activeRestaurant.id,
                  getLocalProduct: getLocalProductById,
                  getLocalProductionLocation: getLocalProductionLocationById,
                });
                if (printedIds.length > 0) {
                  console.log(`[sendItemsToOrder] Offline printed ${printedIds.length} item(s) locally`);
                }
              } catch (printErr) {
                console.error('[sendItemsToOrder] Offline print failed (non-blocking):', printErr);
              }
            }
          }

          toast.success("Pedido salvo offline!", {
            description: "Será sincronizado quando a conexão for restaurada.",
          });

          return {
            id: offlineId,
            order_number: `OFF-${Date.now()}`,
            isOffline: true,
          };
        } catch (offlineError) {
          console.error("Error saving offline:", offlineError);
        }
      }

      throw error;
    }
  };

  // Close an existing order (finalize payment for sent items)
  const closeOrder = async (
    orderId: string,
    discount: number = 0,
    payments: PaymentEntry[] = [],
    serviceTax: number = 0,
    couvertQuantity: number = 0,
    couvertAmount: number = 0,
    tableId?: string,
    commandId?: string,
    entryDifference?: number,
    entryId?: string,
    peopleCount?: number,
    subtotalOverride?: number // For offline mode where we may not fetch from server
  ) => {
    if (!activeRestaurant || !orderId) return null;

    // Calculate total from provided values (for offline mode)
    const calculateTotal = (subtotal: number) => {
      const entryAmount = entryDifference || 0;
      return subtotal - discount + serviceTax + couvertAmount + entryAmount;
    };

    // ---- Helper: close order locally (used by offline path AND as fallback) ----
    const closeOrderLocally = async () => {
      if (!sqliteReady) {
        console.error('[closeOrder] SQLite not ready, cannot close locally');
        toast.error('Erro ao fechar conta: banco local indisponível');
        return null;
      }

      console.log('[closeOrder] Closing order LOCALLY (offline or fallback)');

      try {
        let subtotal = subtotalOverride || 0;
        if (!subtotal) {
          const localItems = getLocalOrderItems(orderId);
          subtotal = localItems.reduce((sum, item) => sum + item.total_price, 0);
          
          // Auto-incorporate: also check active_consumptions cache (IndexedDB)
          // which may have items not yet synced to SQLite order_items
          const entityId = tableId || commandId;
          if (entityId) {
            try {
              const cached = await getActiveConsumptionIDB(entityId);
              if (cached && cached.subtotal > subtotal) {
                console.log(`[closeOrder] Using cached subtotal (${cached.subtotal}) over local items subtotal (${subtotal})`);
                subtotal = cached.subtotal;
              }
            } catch (e) {
              console.warn('[closeOrder] Error reading active consumption cache:', e);
            }
          }
        }

        const total = calculateTotal(subtotal);
        const primaryPayment = payments.length > 0 ? payments[0] : null;
        const paymentMethodId = primaryPayment?.paymentMethod?.id || null;
        const paymentFee = primaryPayment?.feeAmount || 0;

        localCloseOrder(
          orderId, 'delivered', serviceTax, total, tableId, commandId,
          discount, couvertQuantity, couvertAmount,
          paymentMethodId, paymentFee, peopleCount
        );

        // Persist ALL payments to order_payments (sync queue)
        // The primary payment fields on `orders` are kept for trigger compat,
        // but order_payments rows are the source of truth for finance reports.
        try {
          const nowIso = new Date().toISOString();
          for (const p of payments) {
            if (!p?.paymentMethod?.id) continue;
            localAddOrderPayment({
              id: p.id || crypto.randomUUID(),
              order_id: orderId,
              payment_method_id: p.paymentMethod.id,
              payment_method_name: p.paymentMethod.name,
              amount: Number(p.amount) || 0,
              fee_amount: Number(p.feeAmount) || 0,
              created_at: nowIso,
            });
          }
        } catch (payErr) {
          console.error('[closeOrder] Error persisting offline payments:', payErr);
        }

        // Remove from active consumptions cache - BOTH SQLite and IndexedDB
        if (tableId) {
          deleteActiveConsumption(tableId);
          deleteActiveConsumptionIDB(tableId).catch(err =>
            console.error('[closeOrder] Error clearing IndexedDB cache:', err)
          );
        }
        if (commandId) {
          deleteActiveConsumption(commandId);
          deleteActiveConsumptionIDB(commandId).catch(err =>
            console.error('[closeOrder] Error clearing IndexedDB cache:', err)
          );
        }

        toast.success('Conta fechada offline!', {
          description: 'Será sincronizada quando a conexão for restaurada.',
        });

        return {
          id: orderId,
          order_number: 'OFFLINE',
          subtotal,
          total,
          isOffline: true,
        };
      } catch (error) {
        console.error('[closeOrder] Error closing order offline:', error);
        toast.error('Erro ao fechar conta offline');
        return null;
      }
    };

    // Só fecha no SQLite do browser se NÃO há internet NEM servidor local alcançável.
    // Internet caída mas LAN ok → segue para o caminho normal (closeOrderLocal via gateway).
    if (!isOnline && sqliteReady && !(await isLocalServerAvailable())) {
      return closeOrderLocally();
    }

    try {
      // Get the current order to calculate totals.
      // LOCAL-FIRST (gateway): consistente com as escritas local-first (cancel/send-items/close).
      // Evita ler o subtotal DEFASADO da nuvem num cancelar→fechar imediato (a nuvem só recebe o
      // cancelamento ~10s depois, no sync) — o que inflava o total. Sem mesa/comanda resolvida, ou
      // local indisponível, cai para a leitura de nuvem (fallback).
      let existingOrder: any = null;
      const closeResource = tableId ? "tables" : commandId ? "commands" : null;
      const closeResourceId = tableId || commandId;
      if (closeResource && closeResourceId) {
        const localOrder = await getCurrentOrder<Record<string, any>>(closeResource, closeResourceId);
        if (localOrder.kind === "order" && localOrder.order?.id === orderId) {
          existingOrder = localOrder.order;
          if (import.meta.env.DEV) console.debug("[closeOrder] existingOrder do servidor local (subtotal pós-cancelamento)");
        }
      }
      // Fallback nuvem (com retry) — só se o local não resolveu o pedido.
      try {
        if (!existingOrder) existingOrder = await withRetry(
          async () => {
            const { data, error } = await supabase
              .from("orders")
              .select("*")
              .eq("id", orderId)
              .single();
            
            if (error) throw error;
            return data;
          },
          {
            maxRetries: 3,
            baseDelayMs: 500,
            onRetry: (attempt, error) => {
              console.warn(`[closeOrder] Retry ${attempt}/3 fetching order:`, error);
            }
          }
        );
      } catch (fetchError) {
        console.error("[closeOrder] Failed to fetch order after retries, falling back to local:", fetchError);
        return closeOrderLocally();
      }

      if (!existingOrder) {
        console.warn("[closeOrder] Order not found online, falling back to local");
        return closeOrderLocally();
      }

      const subtotal = Number(existingOrder.subtotal);
      const entryAmount = entryDifference || 0;
      const total = subtotal - discount + serviceTax + couvertAmount + entryAmount;

      // For backward compatibility, use first payment method if only one
      const primaryPayment = payments.length > 0 ? payments[0] : null;
      const totalFees = payments.reduce((sum, p) => sum + p.feeAmount, 0);

      // Update the order with payment info
      const updateData: Record<string, any> = {
          discount,
          additional_fees: serviceTax,
          couvert_quantity: couvertQuantity,
          couvert_amount: couvertAmount,
          total,
          status: "delivered",
          payment_method_id: primaryPayment?.paymentMethod.id || null,
          payment_fee: totalFees,
          updated_at: new Date().toISOString(),
        };

      // Só atualizar people_count se foi explicitamente informado no checkout
      if (peopleCount !== undefined && peopleCount !== null && peopleCount > 0) {
        updateData.people_count = peopleCount;
      }

      // === FAST-PATH: núcleo do close no servidor local (gateway) → a nuvem recebe via sync. ===
      // Sucesso aqui faz EARLY-RETURN: o bloco de nuvem abaixo (fallback) fica 100% intacto.
      // command_entries (Consumo Aberto = cloud-only) e a limpeza de cache continuam no hook.
      {
        const closePayload = {
          payments: payments.map((p) => ({
            payment_method_id: p.paymentMethod.id,
            amount: p.amount,
            fee_amount: p.feeAmount,
          })),
          discount,
          additional_fees: serviceTax,
          couvert_quantity: couvertQuantity,
          couvert_amount: couvertAmount,
          total,
          payment_method_id: primaryPayment?.paymentMethod.id ?? null,
          payment_fee: totalFees,
          ...(updateData.people_count !== undefined ? { people_count: updateData.people_count } : {}),
        };
        const r = await closeOrderLocal(orderId, closePayload);
        if (r.kind === "businessError") {
          console.error("[closeOrder] erro de negócio do servidor local:", r.error);
          toast.error("Erro ao fechar conta");
          return null; // erro de negócio → sem fallback
        }
        if (r.kind === "ok") {
          if (import.meta.env.DEV) console.debug("[closeOrder] núcleo fechado no servidor local (nuvem via sync)");
          // command_entries close → DIRETO na nuvem (Consumo Aberto lê da nuvem; tabela cloud-only).
          // BEST-EFFORT: com a internet caída (mas LAN ok), o close já gravou no servidor local; o
          // command_entries não pode BLOQUEAR o fechamento — loga e segue. Reconcilia na reconexão.
          if (entryId) {
            try {
              const { error: ceError } = await supabase
                .from("command_entries")
                .update({
                  status: "closed",
                  consumed_amount: subtotal,
                  closed_at: new Date().toISOString(),
                  order_id: orderId,
                })
                .eq("id", entryId);
              if (ceError) console.warn("[closeOrder] command_entries (best-effort) erro:", ceError.message);
            } catch (e) {
              console.warn("[closeOrder] command_entries (best-effort) falhou (internet caída?):", e);
            }
          }
          // Limpeza otimista do cache local (consumo ativo + status da mesa/comanda direta).
          if (sqliteReady) {
            try {
              if (tableId) {
                deleteActiveConsumption(tableId);
                deleteActiveConsumptionIDB(tableId).catch(() => {});
                updateTableStatus(tableId, "available");
              }
              if (commandId) {
                deleteActiveConsumption(commandId);
                deleteActiveConsumptionIDB(commandId).catch(() => {});
                updateCommandStatus(commandId, "available");
              }
            } catch (e) {
              console.warn("[closeOrder] limpeza cache local (não-crítico):", e);
            }
          }
          // Nudge: useTables refaz o fetch (do servidor local) → o mapa reflete a liberação na hora.
          window.dispatchEvent(new CustomEvent("offline-sync-complete"));
          toast.success(`Pedido #${existingOrder.order_number} finalizado!`);
          return existingOrder;
        }
        // r.kind === "unavailable" → cai para o caminho de NUVEM abaixo (inalterado)
      }

      const { error: updateError } = await supabase
        .from("orders")
        .update(updateData)
        .eq("id", orderId);

      if (updateError) {
        throw updateError;
      }

      // Update table status to available and clear name
      if (tableId) {
        console.log("[closeOrder] Liberando mesa:", tableId);
        const { error: tableError } = await supabase
          .from("tables")
          .update({ 
            status: "available", 
            current_order_id: null,
            name: null // Limpar nickname ao encerrar
          })
          .eq("id", tableId);
        
        if (tableError) {
          console.error("[closeOrder] Erro ao liberar mesa:", tableError);
        } else {
          console.log("[closeOrder] Mesa liberada com sucesso:", tableId);
        }
      }

      // Update command status to available
      if (commandId) {
        await supabase
          .from("commands")
          .update({ 
            status: "available", 
            current_order_id: null,
            customer_name: null
          })
          .eq("id", commandId);
      }

      // Release any grouped tables/commands
      const orderId_forGrouped = orderId;
      const { data: groupedTables } = await supabase
        .from("tables")
        .select("id")
        .eq("grouped_to_order_id", orderId_forGrouped);

      if (groupedTables && groupedTables.length > 0) {
        for (const gt of groupedTables) {
          await supabase
            .from("tables")
            .update({ 
              status: "available", 
              current_order_id: null, 
              grouped_to_order_id: null,
              name: null 
            } as any)
            .eq("id", gt.id);
        }
        console.log(`[closeOrder] Released ${groupedTables.length} grouped tables`);
      }

      // Also release the PRIMARY table that owns this order (current_order_id = orderId)
      // This handles the case where checkout is initiated from a grouped secondary table
      const { data: primaryTables } = await supabase
        .from("tables")
        .select("id")
        .eq("current_order_id", orderId)
        .neq("id", tableId || "00000000-0000-0000-0000-000000000000");

      if (primaryTables && primaryTables.length > 0) {
        for (const pt of primaryTables) {
          await supabase
            .from("tables")
            .update({ 
              status: "available", 
              current_order_id: null,
              name: null 
            })
            .eq("id", pt.id);
        }
        console.log(`[closeOrder] Released ${primaryTables.length} primary tables`);
      }

      const { data: groupedCommands } = await supabase
        .from("commands")
        .select("id")
        .eq("grouped_to_order_id", orderId_forGrouped);

      if (groupedCommands && groupedCommands.length > 0) {
        for (const gc of groupedCommands) {
          await supabase
            .from("commands")
            .update({ 
              status: "available", 
              current_order_id: null, 
              grouped_to_order_id: null,
              customer_name: null 
            } as any)
            .eq("id", gc.id);
        }
        console.log(`[closeOrder] Released ${groupedCommands.length} grouped commands`);
      }

      // Also release the PRIMARY command that owns this order
      const { data: primaryCommands } = await supabase
        .from("commands")
        .select("id")
        .eq("current_order_id", orderId)
        .neq("id", commandId || "00000000-0000-0000-0000-000000000000");

      if (primaryCommands && primaryCommands.length > 0) {
        for (const pc of primaryCommands) {
          await supabase
            .from("commands")
            .update({ 
              status: "available", 
              current_order_id: null,
              customer_name: null 
            })
            .eq("id", pc.id);
        }
        console.log(`[closeOrder] Released ${primaryCommands.length} primary commands`);
      }
      if (entryId) {
        await supabase
          .from("command_entries")
          .update({
            status: "closed",
            consumed_amount: subtotal,
            closed_at: new Date().toISOString(),
            order_id: orderId,
          })
          .eq("id", entryId);
      }

      // Create order payments for split payments
      if (payments.length > 0) {
        const orderPayments = payments.map((payment) => ({
          order_id: orderId,
          payment_method_id: payment.paymentMethod.id,
          amount: payment.amount,
          fee_amount: payment.feeAmount,
        }));

        const { error: paymentsError } = await supabase
          .from("order_payments")
          .insert(orderPayments);

        if (paymentsError) {
          console.error("Error creating order payments:", paymentsError);
        }
      }

      // ====== ONLINE SUCCESS: Clean local SQLite cache for ALL grouped members ======
      if (sqliteReady) {
        try {
          // Clean the direct table/command used for checkout
          if (tableId) {
            deleteActiveConsumption(tableId);
            deleteActiveConsumptionIDB(tableId).catch(err =>
              console.error('[closeOrder] Error clearing IndexedDB cache:', err)
            );
            updateTableStatus(tableId, 'available');
          }
          if (commandId) {
            deleteActiveConsumption(commandId);
            deleteActiveConsumptionIDB(commandId).catch(err =>
              console.error('[closeOrder] Error clearing IndexedDB cache:', err)
            );
            updateCommandStatus(commandId, 'available');
          }

          // Clean ALL grouped tables linked to this order
          if (groupedTables && groupedTables.length > 0) {
            for (const gt of groupedTables) {
              deleteActiveConsumption(gt.id);
              deleteActiveConsumptionIDB(gt.id).catch(err =>
                console.error('[closeOrder] Error clearing grouped table IndexedDB cache:', err)
              );
              updateTableStatus(gt.id, 'available');
            }
            console.log(`[closeOrder] Cleaned local cache for ${groupedTables.length} grouped tables`);
          }

          // Clean ALL primary tables linked to this order
          if (primaryTables && primaryTables.length > 0) {
            for (const pt of primaryTables) {
              deleteActiveConsumption(pt.id);
              deleteActiveConsumptionIDB(pt.id).catch(err =>
                console.error('[closeOrder] Error clearing primary table IndexedDB cache:', err)
              );
              updateTableStatus(pt.id, 'available');
            }
            console.log(`[closeOrder] Cleaned local cache for ${primaryTables.length} primary tables`);
          }

          // Clean ALL grouped commands linked to this order
          if (groupedCommands && groupedCommands.length > 0) {
            for (const gc of groupedCommands) {
              deleteActiveConsumption(gc.id);
              deleteActiveConsumptionIDB(gc.id).catch(err =>
                console.error('[closeOrder] Error clearing grouped command IndexedDB cache:', err)
              );
              updateCommandStatus(gc.id, 'available');
            }
            console.log(`[closeOrder] Cleaned local cache for ${groupedCommands.length} grouped commands`);
          }

          // Clean ALL primary commands linked to this order
          if (primaryCommands && primaryCommands.length > 0) {
            for (const pc of primaryCommands) {
              deleteActiveConsumption(pc.id);
              deleteActiveConsumptionIDB(pc.id).catch(err =>
                console.error('[closeOrder] Error clearing primary command IndexedDB cache:', err)
              );
              updateCommandStatus(pc.id, 'available');
            }
            console.log(`[closeOrder] Cleaned local cache for ${primaryCommands.length} primary commands`);
          }

          console.log("[closeOrder] Online success: Cleaned local cache for all group members");
        } catch (cacheErr) {
          console.error("[closeOrder] Error cleaning local cache (non-blocking):", cacheErr);
        }
      }

      toast.success(`Pedido #${existingOrder.order_number} finalizado!`);

      // Log the order closure
      await logOperation("close", "order", orderId, {
        order_number: existingOrder.order_number,
        total,
        discount,
      });

      // Send WhatsApp notification if discount was applied
      if (discount > 0) {
        notifyDiscount(existingOrder.order_number, discount, total);
      }

      // Print customer receipt
      try {
        const { data: orderItems } = await supabase
          .from("order_items")
          .select("*")
          .eq("order_id", orderId);

        if (orderItems) {
          let entryReceiptData: {
            entryTypeName?: string;
            entryAmount?: number;
            entryMode?: "fixed" | "minimum_consumption";
            entryDifference?: number;
          } = {};
          if (entryId) {
            try {
              const { data: entryRow } = await supabase
                .from("command_entries")
                .select("entry_type_name, amount, entry_mode")
                .eq("id", entryId)
                .maybeSingle();
              if (entryRow) {
                entryReceiptData.entryTypeName = entryRow.entry_type_name;
                entryReceiptData.entryAmount = Number(entryRow.amount);
                entryReceiptData.entryMode = entryRow.entry_mode as "fixed" | "minimum_consumption";
                if (entryRow.entry_mode === "minimum_consumption") {
                  const diff = Number(entryRow.amount) - subtotal;
                  if (diff > 0) entryReceiptData.entryDifference = diff;
                }
              }
            } catch (e) {
              console.warn("[closeOrder] Erro ao buscar entrada para recibo:", e);
            }
          }

          await printCustomerReceipt({
            orderNumber: existingOrder.order_number,
            items: orderItems.map(item => ({
              name: item.product_name,
              quantity: item.quantity,
              unit_price: Number(item.unit_price),
              total_price: Number(item.total_price),
            })),
            subtotal,
            serviceTax,
            discount,
            total,
            paymentMethod: payments[0]?.paymentMethod.name || "Não informado",
            customerName: existingOrder.customer_name || undefined,
            restaurantName: activeRestaurant.name,
            restaurantId: activeRestaurant.id,
            couvertAmount: couvertAmount > 0 ? couvertAmount * (couvertQuantity || 1) : undefined,
            couvertQuantity: couvertQuantity || undefined,
            ...entryReceiptData,
          });
        }
      } catch (printError) {
        console.error("Error printing receipt:", printError);
      }

      // Print exit release for commands if enabled
      if (commandId && (activeRestaurant as any).print_exit_release) {
        try {
          // Fetch command number
          const { data: cmdData } = await supabase
            .from("commands")
            .select("number")
            .eq("id", commandId)
            .single();

          if (cmdData) {
            await printExitRelease({
              commandNumber: cmdData.number,
              employeeName: undefined, // TODO: add operator name when available
              terminalName: getTerminalName() || undefined,
              restaurantName: activeRestaurant.name,
              restaurantId: activeRestaurant.id,
            });
          }
        } catch (exitPrintError) {
          console.error("Error printing exit release:", exitPrintError);
        }
      }

      return existingOrder;
    } catch (error: any) {
      console.error("Error closing order online, falling back to local:", error);
      return closeOrderLocally();
    }
  };

  return {
    createOrder,
    sendItemsToOrder,
    closeOrder,
  };
}
